import { useEffect, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import api from '../../lib/api';

const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');

export function ReturnedInvoiceFixDialog({ open, onOpenChange, invoice, onDone }) {
  const [form, setForm] = useState({ invoice_number: '', invoice_date: '', pembulatan: 0, biaya_angkut: 0, items: [] });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (invoice && open) {
      setForm({
        invoice_number: invoice.invoice_number || '',
        invoice_date: toDateInput(invoice.invoice_date),
        pembulatan: invoice.pembulatan || 0,
        biaya_angkut: invoice.biaya_angkut || 0,
        items: (invoice.items || []).map((it) => ({ ...it })),
      });
    }
  }, [invoice, open]);

  if (!invoice) return null;

  const setItem = (idx, key, val) => {
    setForm((f) => ({ ...f, items: f.items.map((it, i) => (i === idx ? { ...it, [key]: val } : it)) }));
  };
  const removeItem = (idx) => setForm((f) => ({ ...f, items: f.items.filter((_, i) => i !== idx) }));

  const subtotal = form.items.reduce((s, it) => s + (Number(it.quantity) || 0) * (Number(it.unit_price) || 0), 0);

  const submit = async () => {
    if (!form.invoice_number.trim()) { toast.error('Nomor invoice wajib diisi'); return; }
    if (form.items.length === 0) { toast.error('Minimal satu item'); return; }
    setLoading(true);
    try {
      await api.patch(`/invoices-full/${invoice.id}/resubmit`, {
        invoice_number: form.invoice_number,
        invoice_date: form.invoice_date || undefined,
        pembulatan: Number(form.pembulatan) || 0,
        biaya_angkut: Number(form.biaya_angkut) || 0,
        items: form.items.map((it) => ({ ...it, quantity: Number(it.quantity) || 0, unit_price: Number(it.unit_price) || 0 })),
      });
      toast.success('Invoice diperbaiki dan disubmit ulang ke AP');
      onOpenChange(false);
      onDone?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal submit ulang invoice');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="returned-fix-dialog">
        <DialogHeader>
          <DialogTitle>Perbaiki & Submit Ulang Invoice</DialogTitle>
          <DialogDescription>Invoice {invoice.invoice_number} dikembalikan AP. Perbaiki data lalu submit ulang.</DialogDescription>
        </DialogHeader>
        <div className="bg-orange-50 border border-orange-300 rounded p-3 text-sm" data-testid="returned-fix-reason">
          <p className="font-semibold text-orange-800">Alasan dikembalikan</p>
          <p className="text-orange-900">{invoice.return_reason || '-'}</p>
          {invoice.returned_by_name && <p className="text-xs text-orange-700 mt-1">oleh {invoice.returned_by_name}{invoice.returned_at ? ` · ${new Date(invoice.returned_at).toLocaleString('id-ID')}` : ''}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Nomor Invoice</Label>
            <Input value={form.invoice_number} onChange={(e) => setForm({ ...form, invoice_number: e.target.value })} data-testid="returned-fix-number" />
          </div>
          <div>
            <Label>Tanggal Invoice</Label>
            <Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} data-testid="returned-fix-date" />
          </div>
          <div>
            <Label>Pembulatan</Label>
            <Input type="number" value={form.pembulatan} onChange={(e) => setForm({ ...form, pembulatan: e.target.value })} data-testid="returned-fix-pembulatan" />
          </div>
          <div>
            <Label>Biaya Angkut</Label>
            <Input type="number" value={form.biaya_angkut} onChange={(e) => setForm({ ...form, biaya_angkut: e.target.value })} data-testid="returned-fix-angkut" />
          </div>
        </div>
        <div>
          <Label>Item</Label>
          <table className="w-full text-sm mt-1">
            <thead><tr className="bg-slate-50 text-left"><th className="p-2">Nama</th><th className="p-2 w-24">Qty</th><th className="p-2 w-36">Harga</th><th className="p-2 w-8"></th></tr></thead>
            <tbody>
              {form.items.map((it, idx) => (
                <tr key={it.line_id || idx} className="border-b" data-testid={`returned-fix-item-${idx}`}>
                  <td className="p-2">{it.name}</td>
                  <td className="p-2"><Input type="number" value={it.quantity} onChange={(e) => setItem(idx, 'quantity', e.target.value)} data-testid={`returned-fix-qty-${idx}`} /></td>
                  <td className="p-2"><Input type="number" value={it.unit_price} disabled={!!invoice.po_id} onChange={(e) => setItem(idx, 'unit_price', e.target.value)} data-testid={`returned-fix-price-${idx}`} /></td>
                  <td className="p-2"><Button size="sm" variant="ghost" className="text-red-600" onClick={() => removeItem(idx)} data-testid={`returned-fix-remove-${idx}`}>×</Button></td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="text-right text-sm mt-2">Subtotal: <span className="font-mono font-semibold" data-testid="returned-fix-subtotal">Rp {subtotal.toLocaleString('id-ID')}</span></p>
        </div>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading} data-testid="returned-fix-cancel">Batal</Button>
          <Button className="bg-indigo-600 hover:bg-indigo-700" onClick={submit} disabled={loading} data-testid="returned-fix-submit">{loading ? 'Mengirim...' : 'Simpan & Submit Ulang'}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
